import React from 'react';
import { Plan } from '../../types/Plan';
import { CATEGORIAS, EXCLUSIONES_COMUNES, GRUPOS } from '../../data/planes';
import { COLOR_BANDA, agruparPorGrupo } from './planesHelpers';
import PlanIdentidad from './PlanIdentidad';
import BeneficioChip from './BeneficioChip';
import { Card } from '../ui';

export interface TablaComparativaProps {
  planes: Plan[];
}

const GRUPOS_CON_CATEGORIAS = agruparPorGrupo(CATEGORIAS, GRUPOS);

/**
 * Filas = las prestaciones agrupadas por grupo, columnas = planes.
 * Encabezado y primera columna sticky para no perder la referencia al
 * recorrer la tabla. Cada grupo va precedido de una banda de sección a todo
 * el ancho, mismo lenguaje visual que el comparativo impreso.
 *
 * La `Card` es a la vez la superficie y el contenedor que scrollea: anidar un
 * segundo recuadro adentro dejaba dos bordes redondeados desalineados, y un
 * `overflow` intermedio habría roto el `position: sticky` de las cabeceras.
 */
const TablaComparativa: React.FC<TablaComparativaProps> = ({ planes }) => {
  let filaIndex = -1;

  return (
    <div className="space-y-4">
      <Card
        className="relative overflow-x-auto focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gappex-primary"
        role="region"
        aria-label="Comparativa de beneficios por plan. Desplazable horizontalmente."
        tabIndex={0}
      >
        <table className="min-w-full border-separate border-spacing-0 text-sm">
          <thead>
            <tr>
              <th
                scope="col"
                className="sticky left-0 top-0 z-30 min-w-[8.5rem] border-b border-slate-200 bg-white px-3 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-400"
              >
                Prestación
              </th>
              {planes.map((plan) => (
                <th
                  key={plan.id}
                  scope="col"
                  className="sticky top-0 z-20 min-w-[8.25rem] border-b border-slate-200 bg-white px-1.5 py-2.5 text-left align-top"
                >
                  <PlanIdentidad plan={plan} compact />
                </th>
              ))}
            </tr>
          </thead>
          {GRUPOS_CON_CATEGORIAS.map(({ grupo, alcance, categorias }) => (
            <tbody key={grupo}>
              <tr>
                <th
                  scope="rowgroup"
                  colSpan={planes.length + 1}
                  className="border-b border-slate-200 px-3 py-1.5 text-left"
                  style={{ backgroundColor: COLOR_BANDA }}
                >
                  <div className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5">
                    <span className="text-[11px] font-bold uppercase tracking-wider text-white">
                      {grupo}
                    </span>
                    <span className="text-[10px] font-normal normal-case tracking-normal text-white/70">
                      {alcance}
                    </span>
                  </div>
                </th>
              </tr>
              {categorias.map((cat) => {
                filaIndex += 1;
                const rowBg = filaIndex % 2 === 0 ? 'bg-white' : 'bg-slate-50/70';
                return (
                  <tr
                    key={cat.id}
                    className={`group ${rowBg} transition-colors duration-100 ease-out hover:bg-blue-50`}
                  >
                    <th
                      scope="row"
                      className={`sticky left-0 z-10 ${rowBg} border-b border-slate-100 px-1.5 py-2.5 text-left font-normal transition-colors duration-100 ease-out group-hover:bg-blue-50`}
                    >
                      <p className="text-sm text-slate-700">{cat.label}</p>
                      {cat.sublabel && <p className="text-xs text-slate-400">{cat.sublabel}</p>}
                    </th>
                    {planes.map((plan) => (
                      <td
                        key={plan.id}
                        className="border-b border-slate-100 px-2.5 py-2.5 text-center"
                      >
                        <BeneficioChip beneficio={plan.beneficios[cat.id]} label={cat.label} size="sm" />
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          ))}
        </table>
      </Card>

      <Card className="overflow-hidden">
        <div className="px-4 py-2" style={{ backgroundColor: COLOR_BANDA }}>
          <span className="text-[11px] font-bold uppercase tracking-wider text-white">
            Ningún plan aplica descuentos en:
          </span>
        </div>
        <ul className="grid list-disc grid-cols-1 gap-x-6 gap-y-1 px-4 py-3 ps-9 sm:grid-cols-2 lg:grid-cols-3">
          {EXCLUSIONES_COMUNES.map((item) => (
            <li key={item} className="text-sm text-slate-600">
              {item}
            </li>
          ))}
        </ul>
      </Card>
    </div>
  );
};

export default TablaComparativa;
